import api from './api';
import type { Order, CreateOrderData, PaginatedResponse, FilterConfig, OrderStatus, OrderTransaction, OrderDetails, UpdateShippingData } from '../types';

export const orderService = {
  async getOrders(
    page: number = 1,
    pageSize: number = 10,
    filters?: FilterConfig
  ): Promise<PaginatedResponse<Order>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });

    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.status) {
      // Use standardizedStatus for API filtering
      params.append('standardizedStatus', filters.status);
    }
    if (filters?.dateFrom) {
      params.append('dateFrom', filters.dateFrom);
    }
    if (filters?.dateTo) {
      params.append('dateTo', filters.dateTo);
    }

    const response = await api.get<PaginatedResponse<Order>>(`/company/orders?${params.toString()}`);
    return response.data;
  },

  async getOrderById(id: string): Promise<Order> {
    const response = await api.get<Order>(`/company/orders/${id}`);
    return response.data;
  },

  async createOrder(data: CreateOrderData): Promise<Order> {
    const response = await api.post<Order>('/company/orders', data);
    return response.data;
  },

  async updateOrderStatus(id: string, status: OrderStatus): Promise<Order> {
    const response = await api.patch<Order>(`/company/orders/${id}/status`, { status });
    return response.data;
  },

  async deleteOrder(id: string): Promise<void> {
    await api.delete(`/company/orders/${id}`);
  },

  async getRecentOrders(limit: number = 5): Promise<Order[]> {
    const response = await api.get<Order[]>(`/company/orders/recent?limit=${limit}`);
    return response.data;
  },

  async cancelOrder(id: string, reason?: string): Promise<Order> {
    const response = await api.post<Order>(`/company/orders/${id}/cancel`, { reason });
    return response.data;
  },

  async getOrderTransactions(orderId: string): Promise<OrderTransaction[]> {
    const response = await api.get<OrderTransaction[]>(`/company/orders/${orderId}/transactions`);
    return response.data;
  },

  async refundTransaction(orderId: string, transactionId: string): Promise<OrderTransaction> {
    const response = await api.post<OrderTransaction>(`/company/orders/${orderId}/transactions/${transactionId}/refund`);
    return response.data;
  },

  async updateShipping(orderId: string, data: UpdateShippingData): Promise<OrderDetails> {
    const response = await api.patch<OrderDetails>(`/company/orders/${orderId}/shipping`, data);
    return response.data;
  },
};
